import { CITY_DISTRICTS, LAUNCH_CITIES } from '@/data/cityDistricts';

/**
 * Geographic operating scope — the ONE gate every surface reads (owner
 * add-flow, search). Replaces the scattered `Object.keys(CITY_DISTRICTS)` /
 * `LAUNCH_CITIES` / hard-coded `'Warszawa'` checks. Three tiers:
 *
 *  - `active`    — live product: on the map/SERP, tenants search it. Today: Warszawa.
 *  - `supported` — known / next-expansion cities with geometry but not live yet.
 *                  Owners MAY add a listing (collected, NOT shown on the map);
 *                  tenants declare interest via the waiting list.
 *  - `other`     — anything else. No add path; tenant interest captured on the
 *                  waiting list with the concrete city name.
 *
 * Target source of truth is the `supported_cities` DB table (+`is_active` for
 * the active↔supported boundary) — see `docs/GeoScope.md`. Until that lands,
 * the tiers DERIVE from the existing constants so there is no second list to
 * drift: `ACTIVE_CITIES` is `LAUNCH_CITIES` verbatim, `supported` is every city
 * we have district geometry for minus the active ones. Swapping to the DB means
 * changing ONLY this module; callers keep the same interface.
 */
export type CityTier = 'active' | 'supported' | 'other';

/** Live cities (on map/SERP). Single definition, shared with onboarding's LAUNCH_CITIES. */
export const ACTIVE_CITIES: readonly string[] = LAUNCH_CITIES;

/** Known-but-not-live cities: shown "wkrótce" in search; tenant-interest only. */
export const SUPPORTED_CITIES: readonly string[] = Object.keys(CITY_DISTRICTS).filter(
  (c) => !ACTIVE_CITIES.includes(c),
);

/**
 * The single city we currently operate in. Today ACTIVE_CITIES has exactly one
 * entry, so the owner add-flow locks the city to it. When we go multi-city (or
 * to a metro AREA — Warszawa + obwarzanek — see docs/GeoScope.md §metro), this
 * becomes a picker / polygon test; every caller reads it from here so the
 * expansion is a change in THIS module, not a string hunt across the app.
 */
export const PRIMARY_ACTIVE_CITY: string = ACTIVE_CITIES[0];

export function cityTier(city: string | null | undefined): CityTier {
  if (!city) return 'other';
  if (ACTIVE_CITIES.includes(city)) return 'active';
  if (SUPPORTED_CITIES.includes(city)) return 'supported';
  return 'other';
}

/**
 * The gate for BOTH the owner add-flow and live search: only active cities.
 * (Owner add is active-only — a listing in a non-active city can't be built;
 * the owner leaves a supply lead instead. docs/GeoScope.md D4.)
 */
export function isActiveCity(city: string | null | undefined): boolean {
  return cityTier(city) === 'active';
}
